import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useUpdateCart } from "../../components/user/BCPAuthReducers";
import uniqid from "uniqid";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import * as Yup from "yup";
import { Formik } from "formik";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import ListResponsive from "../../components/other/ListResponsive";
import GoogleAddressSearch from "../../components/utilities/GoogleAddressSearch";
import { updateCartItem } from "../../components/user/utils";
import { getData, postData } from "../../components/utilities/UtilityFunctions";

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 7,
    top: 7,
    border: `2px solid ${theme?.palette?.background.paper}`,
    padding: "0 4px",
  },
}));

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {},
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { fileId, alt, outerSX } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      if (!fileId) {
        setImage(null);
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === fileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(fileId);
        if (file) {
          setImage({
            id: fileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [fileId]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "50px",
        minHeight: "50px",
        maxHeight: "50px",
        p: 1,
        border: 1,
        borderColor: "divider",
        ...outerSX,
      }}
    >
      {!loading && image?.url && (
        <img
          src={image.url}
          alt={alt}
          style={{ maxWidth: "100%", maxHeight: "100%" }}
        />
      )}

      {loading && <CircularProgress size={24} />}
    </Box>
  );
};

export default function StoreCheckout() {
  const authContext = useContext(AuthContext);
  const useUpdateCartReducer = useUpdateCart();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const [user, setUser] = useState(null);
  const [validTransaction, setValidTransaction] = useState(null);

  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState([]);

  useEffect(() => {
    const go = async () => {
      setItems(authContext?.authState?.cart?.items || []);
      var newTotals = [];
      var newTotal = { label: "Total", qty: 0, value: 0 };
      if (authContext?.authState?.cart?.items?.length) {
        authContext?.authState?.cart.items.forEach((item) => {
          newTotal.qty += item.qty;
          newTotal.value += item.qty * item.lp;
        });
      }

      newTotals.push(newTotal);

      var apiUser = await getData(
        `/users/${authContext?.authState?.identity?.userId}`,
        {
          queryStringParameters: {
            "expand[]": ["team", "bankAccount", "location"],
          },
        }
      );
      setUser(apiUser);

      newTotals.push({
        label: "Current LP Balance",
        value: apiUser?.loyaltyPoints?.available || 0,
      });

      setValidTransaction(
        (apiUser?.loyaltyPoints?.available || 0) - newTotal.value >= 0
      );

      setTotals(newTotals);
      setLoading(false);
    };

    go();
  }, [authContext]);

  return (
    <>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Formik
            initialValues={{
              saveDeliveryAddress: false,
              email: authContext?.authState?.identity?.email || "",
              firstName:
                authContext?.authState?.identity?.firstName ||
                authContext?.authState?.identity?.given_name ||
                "",
              lastName:
                authContext?.authState?.identity?.lastName ||
                authContext?.authState?.identity?.family_name ||
                "",
              company: "",
              deliveryNotes: "",
              subPremise: user?.location?.subPremise || "",
              address1: `${user?.location?.streetNum || ""} ${
                user?.location?.streetName || ""
              }`.trim(),
              address2: "",
              city: user?.location?.city || "",
              zip: user?.location?.zip || "",
              state: user?.location?.state || "",
              country: user?.location?.country || "",
              ...user?.deliveryAddress,
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .max(128)
                .email("Must be a valid email")
                .required("Required"),
              firstName: Yup.string().max(128).required("Required"),
              lastName: Yup.string().max(128).required("Required"),
              address1: Yup.string().max(255).required("Required"),
              city: Yup.string().max(255).required("Required"),
              zip: Yup.string().max(255).required("Required"),
              state: Yup.string().max(255).required("Required"),
              country: Yup.string().max(255).required("Required"),
            })}
            onSubmit={async (values) => {
              try {
                if (!validTransaction) {
                  postNotification("error", "Insufficient Loyalty Points");
                  return;
                }

                setProcessing(true);

                let deliveryAddress = {
                  ...values,
                };
                delete deliveryAddress.saveDeliveryAddress;

                var body = {
                  apiToken: uniqid(),
                  userId: authContext?.authState?.identity?.userId,
                  transactionType: "Order",
                  status: "Processing",
                  chargeType: "Loyalty Points",
                  amount: 0,
                  lineItems: [],
                  deliveryAddress: deliveryAddress,
                  saveDeliveryAddress: values.saveDeliveryAddress,
                };

                if (items?.length) {
                  items.forEach((item) => {
                    body.amount += item.qty * item.lp;
                    body.lineItems.push({
                      productId: item.id,
                      qty: item.qty,
                    });
                  });
                }

                if (!body.lineItems?.length) {
                  postNotification("error", "There are no items in the cart");
                  return;
                }

                var newTransaction = await postData(`/storetransactions`, {
                  body: body,
                });
                updateCartItem(null, useUpdateCartReducer);
                window.location.assign(
                  `/shop/transaction/${newTransaction.id}`
                );
              } catch (err) {
                postNotification("error", err);
                setProcessing(false);
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
              setFieldTouched,
              setValues,
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container sx={{ gap: 2 }}>
                  <Grid item xs={12} sx={{ p: 2 }}>
                    <Typography variant="h3">Checkout</Typography>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ p: 2 }}>
                    <Typography variant="h5">Shipping Address</Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          name="email"
                          label="Email"
                          value={values.email}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={(e) => {
                            const trimmedString = e.target.value
                              .trim()
                              .toLowerCase();
                            setFieldValue("email", trimmedString);
                          }}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="firstName"
                          label="First Name"
                          value={values.firstName}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.firstName && errors.firstName)}
                          helperText={touched.firstName && errors.firstName}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          name="lastName"
                          label="Last Name"
                          value={values.lastName}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.lastName && errors.lastName)}
                          helperText={touched.lastName && errors.lastName}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="company"
                          label="Company (optional)"
                          value={values.company}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.company && errors.company)}
                          helperText={touched.company && errors.company}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <GoogleAddressSearch
                          label="Address Lookup"
                          setLocationData={(e) => {
                            var address1 = `${
                              e.streetNum !== undefined ? e.streetNum : ""
                            } ${
                              e.streetName !== undefined ? e.streetName : ""
                            }`.trim();
                            delete e.streetNum;
                            delete e.streetName;

                            setValues({
                              ...values,
                              ...e,
                              address1: address1,
                            });
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="subPremise"
                          label="Unit/Apt Number"
                          value={values.subPremise}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.subPremise && errors.subPremise
                          )}
                          helperText={touched.subPremise && errors.subPremise}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="address1"
                          label="Address Line 1"
                          value={values.address1}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.address1 && errors.address1)}
                          helperText={touched.address1 && errors.address1}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="address2"
                          label="Address Line 2"
                          value={values.address2}
                          variant="outlined"
                          fullWidth
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.address2 && errors.address2)}
                          helperText={touched.address2 && errors.address2}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="city"
                          label="City"
                          value={values.city}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.city && errors.city)}
                          helperText={touched.city && errors.city}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="state"
                          label="State"
                          value={values.state}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.state && errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="zip"
                          label="Zip / Post Code"
                          value={values.zip}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.zip && errors.zip)}
                          helperText={touched.zip && errors.zip}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="country"
                          label="Country"
                          value={values.country}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(touched.country && errors.country)}
                          helperText={touched.country && errors.country}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="deliveryNotes"
                          value={values.deliveryNotes}
                          label="Delivery Notes"
                          variant="outlined"
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          multiline
                          rows={5}
                          error={Boolean(
                            touched.deliveryNotes && errors.deliveryNotes
                          )}
                          helperText={
                            touched.deliveryNotes && errors.deliveryNotes
                          }
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel
                          label={
                            <Typography className="MuiFormControlLabel-label">
                              Save Shipping Address
                            </Typography>
                          }
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              color="primary"
                              checked={values.saveDeliveryAddress}
                              onChange={(e) => {
                                setFieldValue(
                                  "saveDeliveryAddress",
                                  !values.saveDeliveryAddress
                                );
                              }}
                            />
                          }
                          labelPlacement="start"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                      p: 2,
                      maxWidth: [
                        "calc(100% ) !important",
                        "calc(100% ) !important",
                        "calc(50% - 18px) !important",
                      ],
                      minWidth: [
                        "calc(100% ) !important",
                        "calc(100% ) !important",
                        "calc(50% - 18px) !important",
                      ],
                    }}
                  >
                    <ListResponsive
                      originalList={items}
                      rowProps={{
                        mapFunction: (row, index) => (
                          <RowItem key={index} row={row} />
                        ),
                      }}
                    />

                    {totals?.length > 0 &&
                      totals.map((total, index) => {
                        return <RowItem key={index} total={total} />;
                      })}
                  </Grid>

                  <Grid item xs={12} sx={{ p: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Button
                          href="/shop/cart"
                          fullWidth
                          variant="outlined"
                          color="primary"
                        >
                          Return to Cart
                        </Button>
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          ml: 2,
                          mr: -2,
                          maxWidth: ["calc(50% - 18px) !important"],
                          minWidth: ["calc(50% - 18px) !important"],
                        }}
                      >
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="success"
                        >
                          Pay Now
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      )}
      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}

const RowItem = (props) => {
  const { heading, row, total } = props;

  return (
    <Grid
      item
      xs={12}
      sx={{
        p: 1,
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={9}>
          {heading ? (
            <Typography variant="body1">Product</Typography>
          ) : total ? (
            <Typography variant="body1">{total.label || "Total"}</Typography>
          ) : (
            <Grid container>
              <Grid item xs sx={{ maxWidth: ["50px", "70px"] }}>
                <StyledBadge badgeContent={row.qty} color="primary">
                  <ImagePreview fileId={row?.imageFileId} alt={row.name} />
                </StyledBadge>
              </Grid>
              <Grid item xs>
                <Typography variant="body1">{row.name}</Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid
          item
          xs={3}
          sx={{
            textAlign: "right",
          }}
        >
          {heading ? (
            <Typography variant="body1">Subtotal</Typography>
          ) : total ? (
            <Typography variant="body1">{total.value} LP</Typography>
          ) : (
            <Typography variant="body1">{row.qty * row.lp} LP</Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
